import type { CurrentUser } from "@/lib/auth";
import type { LieferscheinSummary } from "@/lib/lieferscheine";
import {
  formatInvoiceValue,
  getContactAgeThresholds,
  statusBadgeClass,
  type ContactAgeThreshold,
} from "@/pages/stundennachweise/timesheetFilterLogic";

const AT_CONTACT_STATUSES = new Set(["erstellt", "versendet"]);

export function lieferscheinCanMarkAtContact(row: LieferscheinSummary): boolean {
  if (row.billing_status === "archived" || row.billing_archived_at) return false;
  return AT_CONTACT_STATUSES.has(String(row.status || "").toLowerCase());
}

/** Lieferscheine, die noch beim Ansprechpartner liegen (älteste zuerst). */
export function filterLieferscheineAtContact(rows: LieferscheinSummary[]): LieferscheinSummary[] {
  return rows
    .filter((row) => Boolean(row.at_contact) && lieferscheinCanMarkAtContact(row))
    .sort((a, b) => {
      const ta = Date.parse(String(a.at_contact_at || "")) || 0;
      const tb = Date.parse(String(b.at_contact_at || "")) || 0;
      if (ta !== tb) return ta - tb;
      return Number(a.id || 0) - Number(b.id || 0);
    });
}

export function getLieferscheinContactAgeInfo(
  row: LieferscheinSummary,
  now = Date.now(),
): { ageDays: number; threshold: ContactAgeThreshold } | null {
  if (!row.at_contact || !lieferscheinCanMarkAtContact(row)) return null;
  const markedAt = row.at_contact_at ? new Date(row.at_contact_at) : null;
  if (!markedAt || Number.isNaN(markedAt.getTime())) return null;
  const ageDays = (now - markedAt.getTime()) / (24 * 60 * 60 * 1000);
  const threshold = getContactAgeThresholds().find((entry) => ageDays > entry.days) ?? null;
  if (!threshold) return null;
  return { ageDays, threshold };
}

export function getLieferscheinContactName(row: LieferscheinSummary): string {
  const lastName = String(row.ansprechpartner_nachname || "").trim();
  const firstName = String(row.ansprechpartner_vorname || "").trim();
  const fallback = String(row.ansprechpartner || "").trim();
  if (lastName && firstName) return `${lastName}, ${firstName}`;
  if (lastName) return lastName;
  if (firstName) return firstName;
  return fallback || "Unbekannt";
}

export function getLieferscheinContactSearchTerm(row: LieferscheinSummary): string {
  const lastName = String(row.ansprechpartner_nachname || "").trim();
  if (lastName) return lastName;
  const fallback = String(row.ansprechpartner || "").trim();
  if (fallback) return fallback;
  return String(row.ansprechpartner_vorname || "").trim();
}

export function formatLieferscheinProjectLabel(row: LieferscheinSummary): string {
  const proj = String(row.auftrag_display || row.betreff || "").trim();
  const year = row.jahr != null ? Number(row.jahr) : null;
  if (year && !Number.isNaN(year)) {
    return proj ? `${proj}-${year}` : String(year);
  }
  return proj;
}

function formatDeDate(raw: string | null | undefined): string {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("de-DE");
}

export function formatLieferscheinPeriod(row: LieferscheinSummary): string {
  const start = formatDeDate(row.belegdatum);
  const end = formatDeDate(row.lieferdatum || row.customer_signed_at);
  if (start && end) return start === end ? start : `${start} – ${end}`;
  return start || end || "";
}

export function lieferscheinOrdnerNummer(row: LieferscheinSummary): string {
  const fromAuftrag = String(row.auftragsnummer || "").trim().split("#")[0] || "";
  return (fromAuftrag || row.ordnernummer || "").trim();
}

export function lieferscheinIsS(row: LieferscheinSummary): boolean {
  return lieferscheinOrdnerNummer(row).toUpperCase().startsWith("S");
}

export function lieferscheinYear(row: LieferscheinSummary): number | null {
  if (row.jahr != null && Number.isFinite(Number(row.jahr))) return Number(row.jahr);
  if (row.created_at) {
    const d = new Date(row.created_at);
    if (!Number.isNaN(d.getTime())) return d.getFullYear();
  }
  return null;
}

function statusBadgeKey(row: LieferscheinSummary): string {
  const billing = String(row.billing_status || row.status || "").toLowerCase();
  switch (billing) {
    case "erstellt":
      return "draft";
    case "versendet":
      return "ready";
    case "signed":
    case "geliefert":
    case "assigned":
      return "signed";
    case "invoice_draft":
      return "invoice_draft";
    case "invoice_created":
      return "invoice_created";
    case "archived":
      return "archived";
    default:
      return billing || "draft";
  }
}

export function lieferscheinStatusBadgeClass(
  row: LieferscheinSummary,
  user: CurrentUser | null,
): string {
  return statusBadgeClass(statusBadgeKey(row), user);
}

export { formatInvoiceValue, getContactAgeThresholds };
